import { Component, inject, OnDestroy, OnInit, ViewChild, AfterViewInit } from '@angular/core';
import {
	FormularioRelatorio,
	INPUTS_RELATORIOS,
	InstanciaRelatorio,
} from '@home/submodulos/dados-meteorologicos/interfaces/tipos-relatorios';
import { Validators } from '@angular/forms';
import { FeatureCollection, Polygon, GeoJsonObject } from 'geojson';
import { HttpClient } from '@angular/common/http';
import { Position } from 'pdfmake/interfaces';
import {
	criarCanvasInterpolacao,
	InterpolacaoCanvasBounds,
	TipoValor,
	ValoresBusca,
} from '@utils/interpolacao/interpolacao';
import { ToastrService } from 'ngx-toastr';
import { RelatoriosService } from '@home/submodulos/dados-meteorologicos/services/relatorios.service';
import { DocumentExporter } from '@utils/document-exporter';
import { AgrupamentoResponse, Meses } from '../../../interfaces';
import html2canvas from 'html2canvas';
import * as pdfseira from '@utils/pdf-seira';
import { GroupButton } from '@componentes/public-button-group/public-button-group.component';
import * as ExcelTable from 'mr-excel';
import { DataModel } from 'mr-excel';
import { VariavelMeteorologica } from '@utils/interpolacao/intepolacao-parametros';
import {
	alinharDado,
	baixarEAbrirPDF,
	compararStrings,
	compararStringsIguais,
	isNuloOuUndefined,
	numberToBrNumberTables,
	verificaSePDFEAdicionaNaTabela,
} from '@utils';
import { MapaInterpolacaoComponent } from '../../../submodulos/monitoramento/pages/variaveis/componentes/mapa-interpolacao/mapa-interpolacao.component';
import { NumberUtils } from '@utils/number-utils';
import * as Leaflet from 'leaflet';

type MapCapture = {
	dataUrl: string;
	mapRect: { x: number; y: number; w: number; h: number };
	canvasW: number;
	canvasH: number;
};

@Component({
	selector: 'seira-mapa-climatologia',
	templateUrl: './mapa-climatologia.component.html',
	styleUrls: ['./mapa-climatologia.component.scss'],
})
export class MapaClimatologiaComponent
	implements OnInit, OnDestroy, AfterViewInit, InstanciaRelatorio
{
	inputs = inject(INPUTS_RELATORIOS);
	climatologias: AgrupamentoResponse[] = [];
	regiao?: Position[][];
	carregando = true;
	interpolacao?: InterpolacaoCanvasBounds;
	descricaoRelatorio =
		'Define-se como climatologia pluviométrica, a média histórica de pluviometria para períodos longos, preferencialmente de 30 anos.';
	periodoTitulo = '';
	mapa: MapaInterpolacaoComponent;
	@ViewChild(MapaInterpolacaoComponent, { static: false })
	set mapaInterpolacao(mapa: MapaInterpolacaoComponent) {
		if (mapa) {
			this.mapa = mapa;

			setTimeout(() => this.instalarOverlaysNoMapa(), 0);
		}
	}
	mapaId = 'climatologia';
	relatorioService = inject(RelatoriosService);

	private isExporting = false;
	private readonly exportScale = 2;
	private readonly exportDesktopWidth = 993;
	private readonly exportMapHeight = 800;

	private coordOverlayEl?: HTMLDivElement;
	private coordOverlayCleanup?: () => void;

	private compassOverlayEl?: HTMLDivElement;
	private compassOverlayCleanup?: () => void;
	private leafletMap?: Leaflet.Map;

	private pbGeo?: GeoJsonObject;
	maskLayer?: Leaflet.Polygon;
	private maskPaneName = 'maskPane';
	private outlineLayer?: Leaflet.GeoJSON;
	private outlinePaneName = 'outlinePane';
	private contornoPaneName = 'contornoPane';

	private readonly coordenadasRef = {
		eixoVertical: [
			{ lat: -5.9, label: '6.0° S' },
			{ lat: -6.5, label: '6,5° S' },
			{ lat: -7.0, label: '7,0° S' },
			{ lat: -7.5, label: '7,5° S' },
			{ lat: -8.0, label: '8,0° S' },
			{ lat: -8.4, label: '' },
		],
		eixoHorizontal: [
			{ lng: -38.5, label: '38,5° W' },
			{ lng: -38.0, label: '38,0° W' },
			{ lng: -37.5, label: '37.5° W' },
			{ lng: -37.0, label: '37,0° W' },
			{ lng: -36.5, label: '36,5° W' },
			{ lng: -36.0, label: '36,0° W' },
			{ lng: -35.5, label: '35,5° W' },
			{ lng: -35.0, label: '35,0° W' },
		],
	};

	botoesDeExportacao: GroupButton[] = [
		{
			label: '.png',
			size: 'small',
			icon: 'ph-image',
			onClick: async () => {
				if (this.mapa) this.mapa.resetMapView();
				setTimeout(() => this.exportarPNG(), 1000);
			},
		},

		{
			label: '.pdf',
			size: 'small',
			icon: 'ph-file-pdf',
			activeFakeLoading: true,
			onClick: () => {
				if (this.mapa) {
					this.mapa.resetMapView();
				}
				setTimeout(() => {
					return this.exportarPDF();
				}, 1000);
			},
		},
		{
			label: '.csv',
			size: 'small',
			icon: 'ph-file-csv',
			onClick: () => this.exportarCSV(),
		},
		{
			label: '.txt',
			size: 'small',
			icon: 'ph-file-text',
			onClick: () => this.exportarTXT(),
		},
		{
			label: '.xlsx',
			size: 'small',
			icon: 'ph-file-xls',
			onClick: () => this.exportarXLSX(),
		},
	];

	constructor(
		private httpClient: HttpClient,
		private relatoriosService: RelatoriosService,
		private toastr: ToastrService
	) {
		this.setValidators();
	}

	ngOnInit() {
		this.initialValues();
	}

	ngOnDestroy() {
		this.inputs.form.get(FormularioRelatorio.MES_INICIO)?.clearValidators();
		this.inputs.form.get(FormularioRelatorio.MES_FIM)?.clearValidators();

		if (this.maskLayer) {
			this.maskLayer.remove();
			this.maskLayer = undefined;
		}

		if (this.coordOverlayEl) this.coordOverlayEl.remove();
		if (this.coordOverlayCleanup) this.coordOverlayCleanup();

		if (this.compassOverlayEl) this.compassOverlayEl.remove();
		if (this.compassOverlayCleanup) this.compassOverlayCleanup();
		if (this.outlineLayer) {
			this.outlineLayer.remove();
			this.outlineLayer = undefined;
		}
	}

	initialValues() {
		const pbGeoJsonObservable = this.httpClient.get<FeatureCollection>(
			'assets/geoJson/pb-geo.json'
		);
		pbGeoJsonObservable.subscribe(value => {
			this.pbGeo = value as unknown as GeoJsonObject;
			this.regiao = (<Polygon>value.features[0].geometry)
				.coordinates as Position[][];
		});
	}

	ngAfterViewInit(): void {
		this.instalarOverlaysNoMapa();
	}

	onMapReady(map: Leaflet.Map) {
		this.leafletMap = map;
		map.attributionControl?.remove();
	}

	setValidators() {
		this.inputs.form
			.get(FormularioRelatorio.MES_INICIO)
			?.setValidators(Validators.required);
		this.inputs.form
			.get(FormularioRelatorio.MES_FIM)
			?.setValidators(Validators.required);
	}

	gerarRelatorio() {
		if (this.inputs.form.invalid) {
			return;
		}

		const mesInicio = this.inputs.form.get(FormularioRelatorio.MES_INICIO)
			?.value;
		const mesFim = this.inputs.form.get(FormularioRelatorio.MES_FIM)?.value;

		if (!mesInicio || !mesFim) {
			return;
		}

		const indiceInicio = Object.values(Meses).indexOf(
			Object.values(Meses).find(mes => compararStrings(mes, mesInicio))!
		);
		const indiceFim = Object.values(Meses).indexOf(
			Object.values(Meses).find(mes => compararStrings(mes, mesFim))!
		);

		const qtdMeses = indiceFim - indiceInicio + 1;

		if (qtdMeses === 1) {
			this.mapaId = 'climatologiaMes';
		} else if (qtdMeses >= 2 && qtdMeses <= 3) {
			this.mapaId = 'climatologiaTrimestral';
		} else if (qtdMeses === 4 || qtdMeses === 5) {
			this.mapaId = 'climatologiaQuadra';
		} else if (qtdMeses === 6) {
			this.mapaId = 'climatologiaSemestral';
		} else if (
			compararStringsIguais(mesInicio, Meses.JANEIRO) &&
			compararStringsIguais(mesFim, Meses.DEZEMBRO)
		) {
			this.mapaId = 'climatologiaAno';
		} else {
			// fallback: usa Semestral para intervalos maiores que 6 mas menores que 12
			this.mapaId = 'climatologiaSemestral';
		}

		this.inputs.setLoading(true);
		this.carregando = true;

		const mesInicioFormatado = Object.values(Meses).find(mes =>
			compararStrings(mes, mesInicio)
		);
		const mesFimFormatado = Object.values(Meses).find(mes =>
			compararStrings(mes, mesFim)
		);

		this.periodoTitulo = `${mesInicioFormatado} a ${mesFimFormatado}`;

		this.relatoriosService
			.buscarClimatologia(mesInicio.toLowerCase(), mesFim.toLowerCase())
			.subscribe({
				next: (resp: []) => {
					const collator = new Intl.Collator('pt-BR', {
						sensitivity: 'base',
						numeric: true,
					});
					resp.sort((a: AgrupamentoResponse, b: AgrupamentoResponse) => {
						const nomeA = a.nomeOriginal ?? a.nome;
						const nomeB = b.nomeOriginal ?? b.nome;
						return collator.compare(nomeA, nomeB);
					});
					this.relatorioService.atualizaVisibilidadeBotoesExportacao(resp);
					this.climatologias = resp;
				},
				error: () => {
					this.carregando = false;
					this.inputs.setLoading(false);
				},
				complete: () => {
					this.interpolarValores();
					this.carregando = false;
					this.inputs.setLoading(false);
				},
			});
	}

	interpolarValores() {
		this.carregando = true;
		if (this.climatologias.length > 0) {
			this.criarImagemInterpolacao().then(interpolacao => {
				this.interpolacao = interpolacao as InterpolacaoCanvasBounds;
				this.carregando = false;
			});
		} else {
			this.interpolacao = undefined;
			this.carregando = false;
			this.toastr.warning('Não existem dados a serem exibidos.');
		}
	}

	criarImagemInterpolacao() {
		const valores: ValoresBusca[] = this.climatologias.map((value: any) => {
			return {
				lat: value.latitude,
				lng: value.longitude,
				value: value.total,
				municipio: value.nome,
				unidade: 'mm',
				tipoValor: TipoValor.CLIMATOLOGIA,
			};
		});
		return new Promise((resolve, reject) => {
			const interpolacao = criarCanvasInterpolacao(
				valores,
				this.mapaId as VariavelMeteorologica,
				this.regiao as number[][][],
				0.01,
				true
			);
			if (interpolacao === undefined || interpolacao === null) {
				reject();
			}
			resolve(interpolacao);
		});
	}

	getDadosTabelaParaExportacao(dados: AgrupamentoResponse[], isPdf: boolean) {
		const tableData: any[][] = [];
		const colunas = [
			{ text: 'Município', fillColor: '#DCDCDC', alignment: 'center' },
			{ text: 'Climatologia (mm)', fillColor: '#DCDCDC', alignment: 'center' },
		];

		verificaSePDFEAdicionaNaTabela(tableData, colunas, isPdf);

		dados.forEach((item: AgrupamentoResponse) => {
			const rowData = [
				alinharDado(item.nomeOriginal ?? item.nome, isPdf),
				alinharDado(numberToBrNumberTables(item.total, 1), isPdf),
			];
			tableData.push(rowData);
		});

		return tableData;
	}

	private get nomeExportacaoBase(): string {
		return `Climatologia (mm) - ${this.periodoTitulo}`;
	}

	async getMapImage(): Promise<MapCapture | null> {
		const root = document.getElementById('mapa-legenda');
		const map = this.getLeafletMap();

		if (!root || !map) return null;

		this.isExporting = true;
		root.classList.add('export-mode');
		let restoreMobileLayout: (() => void) | null = null;
		const controlesParaOcultar = Array.from(
			root.querySelectorAll(
				'.leaflet-control-zoom, .leaflet-control-container .leaflet-bar, .leaflet-control-container button'
			)
		) as HTMLElement[];

		const estadosOriginais = controlesParaOcultar.map(el => ({
			el,
			display: el.style.display,
		}));

		controlesParaOcultar.forEach(el => {
			el.style.display = 'none';
		});
		this.aplicarContornoComoObservada();
		this.aplicarOverlaysSomenteNaExportacao(map);

		try {
			restoreMobileLayout = await this.ajustarLayoutMobileParaExportacao(
				root,
				map
			);
			await this.esperarRenderizacaoMapa();

			const canvas = await html2canvas(root, {
				useCORS: true,
				allowTaint: true,
				logging: false,
				scale: this.exportScale,
				windowWidth: this.isMobileViewport()
					? this.exportDesktopWidth
					: window.innerWidth,
			});

			const leafletEl = root.querySelector(
				'.leaflet-container'
			) as HTMLElement | null;
			if (!leafletEl) return null;

			const rootRect = root.getBoundingClientRect();
			const mapRectDom = leafletEl.getBoundingClientRect();

			const relLeft = mapRectDom.left - rootRect.left;
			const relTop = mapRectDom.top - rootRect.top;

			return {
				dataUrl: canvas.toDataURL('image/png', 1),
				canvasW: canvas.width,
				canvasH: canvas.height,
				mapRect: {
					x: relLeft * this.exportScale,
					y: relTop * this.exportScale,
					w: mapRectDom.width * this.exportScale,
					h: mapRectDom.height * this.exportScale,
				},
			};
		} finally {
			if (restoreMobileLayout) restoreMobileLayout();
			this.isExporting = false;
			root.classList.remove('export-mode');
			estadosOriginais.forEach(({ el, display }) => {
				el.style.display = display;
			});
			this.aplicarContornoComoObservada();
			this.removerOverlaysDaExportacao();
		}
	}

	async exportarPDF() {
		await new Promise(resolve => setTimeout(resolve, 750));
		const documentDefinition: any = await pdfseira.documentDefinitions();

		const capture = await this.getMapImage();
		if (!capture) {
			console.error('Não foi possível capturar a imagem do mapa.');
			return;
		}
		const { dataUrl: imgDataUrl } = await this.montarMapaComLogos(capture, {
			showHeader: false,
			offsetY: 0,
			showPeriodo: false,
		});

		documentDefinition.content.push([
			{
				text: `Climatologia (mm) - ${this.periodoTitulo}`,
				alignment: 'center',
				margin: [0, 6, 0, 6],
			},
			{
				image: imgDataUrl,
				fit: [500, 400],
				alignment: 'center',
				margin: [0, 18, 0, 0],
			},
		]);

		const tableData = this.getDadosTabelaParaExportacao(
			this.climatologias,
			true
		);

		documentDefinition.content.push({
			table: {
				widths: ['50%', '50%'],
				body: tableData,
				layout: {
					noWrap: false,
					fontSize: 5,
				},
			},

			marginLeft: 15,
			marginRight: 15,
		});

		baixarEAbrirPDF(
			`Climatologia (mm) - ${this.periodoTitulo}`,
			documentDefinition
		);
	}

	async exportarPNG() {
		await new Promise(r => setTimeout(r, 750));

		const capture = await this.getMapImage();
		if (!capture) {
			console.error('Não foi possível capturar a imagem do mapa.');
			return;
		}

		const nome = this.nomeExportacaoBase;

		try {
			const isMobile = this.isMobileViewport();
			const offsetY = isMobile ? 240 : 200;
			const cropPad = isMobile
				? { bottom: 170, top: 65, left: 80, right: 80 }
				: { bottom: 160, top: -10, left: 100, right: 100 };

			const { dataUrl: dataUrlFinal } = await this.montarMapaComLogos(capture, {
				showPeriodo: true,
				periodoYOffset: 2,
				showHeader: true,
				offsetY,
			});

			const recortado = await this.recortarDataUrlSides(
				dataUrlFinal,
				{
					x: capture.mapRect.x,
					y: offsetY + capture.mapRect.y,
					w: capture.mapRect.w,
					h: capture.mapRect.h,
				},
				cropPad
			);

			this.downloadDataUrl(nome, recortado);
		} catch (e) {
			console.error('[Export PNG] Erro ao recortar:', e);
			this.downloadDataUrl(nome, capture.dataUrl);
		}
	}

	exportarCSV() {
		const tableData = this.getDadosTabelaParaExportacao(
			this.climatologias,
			false
		);

		DocumentExporter.gerarCSV(
			tableData,
			`Climatologia (mm) - ${this.periodoTitulo}`
		);
	}

	exportarTXT() {
		const tableData = this.getDadosTabelaParaExportacao(
			this.climatologias,
			false
		);

		let txtData = '';
		if (tableData.length > 0) {
			const colunas = tableData[0];

			tableData.slice(1).forEach(dadoRelatorio => {
				colunas.forEach((coluna: string, index: number) => {
					const valor = dadoRelatorio[index];
					txtData += `${coluna}: ${valor ?? '-'}\n`;
				});

				txtData += '\n';
			});
		}

		DocumentExporter.gerarTXT(
			txtData,
			`Climatologia (mm) - ${this.periodoTitulo}`
		);
	}

	exportarXLSX() {
		const nomeArquivo = `Climatologia (mm) - ${this.periodoTitulo}`;

		const colorPalette = {
			c1: '2C3639',
			c2: 'FFFFFF',
			c3: '000000',
			c4: 'EEEEEE',
		};

		const rowStyle = {
			backgroundColor: colorPalette.c2,
			color: colorPalette.c3,
		};
		const headerStyle = {
			backgroundColor: colorPalette.c4,
			color: colorPalette.c1,
			bold: true,
		};
		const headerStyleCenter = {
			backgroundColor: colorPalette.c4,
			color: colorPalette.c1,
			bold: true,
			alignment: {
				horizontal: 'center',
				vertical: 'center',
			} as DataModel.AlignmentOption,
		};
		const rowStyleCenter = {
			backgroundColor: colorPalette.c2,
			color: colorPalette.c3,
			alignment: {
				horizontal: 'center',
				vertical: 'center',
			} as DataModel.AlignmentOption,
		};

		const title = {
			backgroundColor: colorPalette.c2,
			whiteSpace: 'pre',
			color: colorPalette.c3,
			bold: true,
			alignment: {
				horizontal: 'center',
				vertical: 'center',
				wrapText: 1,
			} as DataModel.AlignmentOption,
		};

		const headers = [
			{
				label: 'nome',
				text: 'Município',
				size: 48,
			},
			{ label: 'total', text: 'Climatologia (mm)', size: 24 },
		];
		const dataExcel = {
			styles: {
				headerStyle,
				headerStyleCenter,
				rowStyle,
				rowStyleCenter,
				title,
			},
			sheet: [
				{
					shiftTop: 3,
					images: [
						{
							url: 'assets/images/cabecalho/cabeçalho_excel.png',
							from: 'A1',
							to: 'C3',
						},
					],
					styleCellCondition(data: any) {
						if (data.label) {
							return 'headerStyleCenter';
						} else {
							return 'rowStyleCenter';
						}
					},
					headers: headers,
					data: this.climatologias.map(({ nome, total }) => {
						return {
							nome: nome,
							total: isNuloOuUndefined(total)
								? '-'
								: NumberUtils.getIgnorandoCasasDecimais(total),
						};
					}),
					columns: [
						{ key: 'nome' },
						{
							key: 'total',
							style: { numFmt: '0.0' },
						},
					],
					title: {
						consommeRow: 3,
						consommeCol: 3,
						text: nomeArquivo,
						styleId: 'title',
						alignment: 'center',
					},
				},
			],
			fileName: nomeArquivo,
		};

		ExcelTable.generateExcel(dataExcel);
	}

	private downloadDataUrl(nomeArquivoSemExt: string, dataUrl: string) {
		const a = document.createElement('a');
		a.href = dataUrl;
		a.download = `${nomeArquivoSemExt}.png`;
		a.style.display = 'none';
		document.body.appendChild(a);
		a.click();
		document.body.removeChild(a);
	}

	private resolveAsset(path: string) {
		return new URL(path, document.baseURI).toString();
	}

	private async montarMapaComLogos(
		capture: MapCapture,
		opts?: {
			showPeriodo?: boolean;
			periodoYOffset?: number;
			showHeader?: boolean;
			offsetY?: number;
		}
	): Promise<{ dataUrl: string; headerH: number }> {
		const showPeriodo = opts?.showPeriodo ?? false;
		const periodoYOffset = opts?.periodoYOffset ?? 0;

		const showHeader = opts?.showHeader ?? true;
		const offsetY = opts?.offsetY ?? 200;
		const logoGovernoSrc = this.resolveAsset(
			'assets/images/logos/governo_pb.png'
		);
		const logoAesaSrc = this.resolveAsset('assets/images/logos/logoAesa.svg');

		const results = await Promise.allSettled([
			this.carregarImagem(capture.dataUrl),
			this.carregarImagem(logoGovernoSrc),
			this.carregarImagem(logoAesaSrc),
		]);

		const mapImage =
			results[0].status === 'fulfilled' ? results[0].value : null;
		const logoGov = results[1].status === 'fulfilled' ? results[1].value : null;
		const logoAesa =
			results[2].status === 'fulfilled' ? results[2].value : null;

		if (!mapImage) return { dataUrl: capture.dataUrl, headerH: 0 };

		const canvas = document.createElement('canvas');
		canvas.width = mapImage.naturalWidth;
		canvas.height = mapImage.naturalHeight + offsetY;

		const ctx = canvas.getContext('2d')!;
		ctx.fillStyle = '#ffffff';
		ctx.fillRect(0, 0, canvas.width, canvas.height);

		ctx.drawImage(mapImage, 0, offsetY);

		ctx.fillStyle = '#ffffff';

		const mapX0 = capture.mapRect.x;
		const mapY0 = offsetY + capture.mapRect.y;
		const mapX1 = mapX0 + capture.mapRect.w;

		const insidePad = 75;
		const maxLogoHInside = Math.round(Math.min(70, capture.mapRect.h * 0.12));

		const drawLogoInside = (imgEl: HTMLImageElement, x: number, y: number) => {
			const ratio = imgEl.naturalWidth / imgEl.naturalHeight;
			const h = maxLogoHInside;
			const w = Math.round(h * ratio);

			ctx.closePath();
			ctx.fill();
			ctx.stroke();
			ctx.restore();

			ctx.drawImage(imgEl, x, y, w, h);
		};

		const mapY1 = mapY0 + capture.mapRect.h;

		const hLogo = maxLogoHInside;
		const isMobile = this.isMobileViewport();
		const logoDy = isMobile ? -90 : -30;

		const govDx = 90;
		const govDy = logoDy;

		const aesaDx = -105;
		const aesaDy = logoDy;

		if (logoAesa) {
			drawLogoInside(
				logoAesa,
				mapX0 + insidePad + govDx,
				mapY1 - insidePad - hLogo + govDy
			);
		}

		if (logoGov) {
			const wG = Math.round(
				hLogo * (logoGov.naturalWidth / logoGov.naturalHeight)
			);
			drawLogoInside(
				logoGov,
				mapX1 - insidePad - wG + aesaDx,
				mapY1 - insidePad - hLogo + aesaDy
			);
		}

		let headerH = 0;
		if (showHeader) {
			headerH = Math.max(52, Math.min(90, Math.round(canvas.height * 0.085)));
			const isMobile = this.isMobileViewport();
			const titleYOffset = isMobile ? 18 : 0;

			ctx.save();
			ctx.fillStyle = 'rgba(255,255,255,0.92)';
			ctx.fillRect(0, 0, canvas.width, headerH);

			ctx.strokeStyle = 'rgba(17,24,39,0.12)';
			ctx.lineWidth = 1;
			ctx.beginPath();
			ctx.moveTo(0, headerH - 0.5);
			ctx.lineTo(canvas.width, headerH - 0.5);
			ctx.stroke();

			const titulo = 'Climatologia (mm)';
			const periodo = this.periodoTitulo ?? '';

			ctx.textAlign = 'center';
			ctx.textBaseline = 'middle';

			const font1 = Math.max(24, Math.min(36, Math.round(headerH * 0.5)));
			const font2 = Math.max(24, Math.min(36, Math.round(headerH * 0.5)));

			ctx.fillStyle = '#111827';
			ctx.font = `700 ${font1}px Poppins, Arial, sans-serif`;
			ctx.fillText(titulo, canvas.width / 2, headerH * 2.8 + titleYOffset - 6);

			if (showPeriodo && periodo.trim()) {
				ctx.font = `600 ${font2}px Poppins, Arial, sans-serif`;
				ctx.fillText(
					periodo,
					canvas.width / 2,
					headerH * 3.3 + titleYOffset + (periodoYOffset ?? 0) - 6
				);
			}

			ctx.restore();
		}

		return { dataUrl: canvas.toDataURL('image/png', 1), headerH };
	}

	private carregarImagem(src: string): Promise<HTMLImageElement> {
		return new Promise((resolve, reject) => {
			const image = new Image();
			image.crossOrigin = 'anonymous';
			image.onload = () => resolve(image);
			image.onerror = err => reject(err);
			image.src = src;
		});
	}

	private async recortarDataUrlSides(
		dataUrl: string,
		rect: { x: number; y: number; w: number; h: number },
		pad: { top?: number; right?: number; bottom?: number; left?: number } = {}
	): Promise<string> {
		const img = await this.carregarImagem(dataUrl);

		const top = pad.top ?? 0;
		const right = pad.right ?? 0;
		const bottom = pad.bottom ?? 10;
		const left = pad.left ?? 0;

		const sx = Math.max(0, Math.floor(rect.x - left));
		const sy = Math.max(0, Math.floor(rect.y - top));
		const sw = Math.min(
			img.naturalWidth - sx,
			Math.floor(rect.w + left + right)
		);
		const sh = Math.min(
			img.naturalHeight - sy,
			Math.floor(rect.h + top + bottom)
		);

		const out = document.createElement('canvas');
		out.width = sw;
		out.height = sh;

		const ctx = out.getContext('2d')!;
		ctx.drawImage(img, sx, sy, sw, sh, 0, 0, sw, sh);

		return out.toDataURL('image/png', 1);
	}

	private aplicarCoordenadas(map: Leaflet.Map) {
		if (this.coordOverlayEl) this.coordOverlayEl.remove();
		if (this.coordOverlayCleanup) this.coordOverlayCleanup();

		const container = map.getContainer() as HTMLElement;
		if (getComputedStyle(container).position === 'static') {
			container.style.position = 'relative';
		}

		const overlay = document.createElement('div');
		overlay.className = 'mapa-coordenadas-overlay';
		overlay.style.position = 'absolute';
		overlay.style.inset = '0';
		overlay.style.pointerEvents = 'none';
		overlay.style.overflow = 'hidden';
		overlay.style.zIndex = '9999';

		container.appendChild(overlay);
		this.coordOverlayEl = overlay;

		const render = () => {
			const b = this.getGeoJsonBounds();
			this.renderizarCoordenadasNoMapa(map, b);
		};

		map.whenReady(() => render());
		map.on('resize', render);
		map.on('moveend', render);
		map.on('zoomend', render);

		this.coordOverlayCleanup = () => {
			map.off('resize', render);
			map.off('moveend', render);
			map.off('zoomend', render);
		};
	}

	private renderizarCoordenadasNoMapa(
		map: Leaflet.Map,
		_bounds?: Leaflet.LatLngBounds
	) {
		if (!this.coordOverlayEl) return;

		const size = map.getSize();
		if (!size || size.x < 50 || size.y < 50) return;

		const b = _bounds ?? map.getBounds();
		const center = b.getCenter();
		const isMobile = this.isMobileViewport();
		const gutterLeft = isMobile ? 60 : 74;
		const padTop = isMobile ? 40 : 60;
		const padRight = isMobile ? 75 : 84;

		const axisX = gutterLeft;
		const axisTop = padTop;
		const axisRight = size.x - padRight;

		const latBaseEixoX =
			this.coordenadasRef.eixoVertical[
				this.coordenadasRef.eixoVertical.length - 1
			]?.lat ?? -8.39;

		const yBaseRaw = map.latLngToContainerPoint([latBaseEixoX, center.lng]).y;

		const normalLine = 'rgba(120, 120, 120, 0.45)';
		const normalText = '#7a7a7a';

		const lineColor = this.isExporting ? 'rgba(0,0,0,1)' : normalLine;
		const textColor = this.isExporting ? 'rgba(0,0,0,1)' : normalText;

		const fontSize = 12;
		const tickLen = 10;

		const gap = 3;

		const maxAxisY = size.y - (tickLen + 18);
		const minAxisY = axisTop + 40;

		const axisY = Math.round(Math.min(maxAxisY, Math.max(minAxisY, yBaseRaw)));

		const eixoLinhas = `
  <!-- esquerda -->
  <div style="position:absolute; left:${axisX}px; top:${axisTop}px; width:1px;
    height:${Math.max(0, axisY - axisTop)}px; background:${lineColor};"></div>

  <!-- baixo -->
  <div style="position:absolute; left:${axisX}px; top:${axisY}px; height:1px;
    width:${Math.max(0, axisRight - axisX)}px; background:${lineColor};"></div>

  ${
		this.isExporting
			? `
        <!-- cima (só export) -->
        <div style="position:absolute; left:${axisX}px; top:${axisTop}px; height:1px;
          width:${Math.max(
						0,
						axisRight - axisX
					)}px; background:${lineColor};"></div>

        <!-- direita (só export) -->
        <div style="position:absolute; left:${axisRight}px; top:${axisTop}px; width:1px;
          height:${Math.max(
						0,
						axisY - axisTop
					)}px; background:${lineColor};"></div>
      `
			: ''
	}
`;
		const ticksY = this.coordenadasRef.eixoVertical
			.map(t => {
				const yRaw = map.latLngToContainerPoint([t.lat, center.lng]).y;
				if (yRaw < axisTop - 80 || yRaw > axisY + 80) return '';

				const y = Math.round(Math.min(axisY, Math.max(axisTop, yRaw)));

				const xLabel = axisX - tickLen - gap;

				return `
			  <!-- label (wrapper posiciona; filho rotaciona) -->
			  <div style="
				position:absolute;
				left:${xLabel}px;
				top:${y}px;
				transform: translate(-100%,-50%);
				color:${textColor};
				font-size:${fontSize}px;
				line-height:1;
				white-space:nowrap;
			  ">
				<span style="
				  display:inline-block;
				  transform: rotate(-90deg);
				  transform-origin: 100% 50%;
				">${t.label}</span>
			  </div>
		
			  <!-- tick -->
			  <div style="
				position:absolute;
				left:${axisX}px;
				top:${y}px;
				width:${tickLen}px;
				height:1px;
				background:${lineColor};
				transform:translateY(-50%);
			  "></div>
			`;
			})
			.join('');

		const ticksX = this.coordenadasRef.eixoHorizontal
			.map((t: { lng: number; label: string }) => {
				const xRaw = map.latLngToContainerPoint([center.lat, t.lng]).x;

				if (xRaw < axisX - 80 || xRaw > axisRight + 80) return '';

				const x = Math.round(Math.min(axisRight, Math.max(axisX, xRaw)));

				return `
				<div style="
				  position:absolute;
				  left:${x}px;
				  top:${axisY - tickLen}px;
				  width:1px;
				  height:${tickLen}px;
				  background:${lineColor};
				  transform:translateX(-50%);
				"></div>
		
				<div style="
				  position:absolute;
				  left:${x}px;
				  top:${axisY + tickLen + -5}px;
				  transform:translateX(-50%);
				  color:${textColor};
				  font-size:${fontSize}px;
				  line-height:1;
				  white-space:nowrap;
				">${t.label}</div>
			  `;
			})
			.join('');

		const midY = Math.round((axisTop + axisY) / 2);
		const midX = Math.round((axisX + axisRight) / 2);

		const titulosEixos = this.isExporting
			? `
				<!-- Latitude (eixo Y) -->
				<div style="
					position:absolute;
					left:${axisX - 30}px;
					top:${midY}px;
					transform: translate(-100%,-50%) rotate(-90deg);
					transform-origin: 100% 50%;
					color:${textColor};
					font-size:${fontSize + 2}px;
					font-weight:0;
					white-space:nowrap;
				">Latitude</div>
		
				<!-- Longitude (eixo X) -->
				<div style="
					position:absolute;
					left:${midX}px;
					top:${axisY + tickLen + 12}px;
					transform: translateX(-50%);
					color:${textColor};
					font-size:${fontSize + 2}px;
					font-weight:350;
					white-space:nowrap;
				">Longitude</div>
			`
			: '';

		this.coordOverlayEl.innerHTML = `${eixoLinhas}${ticksY}${ticksX}${titulosEixos}`;
	}

	private aplicarRosaDosVentos(map: Leaflet.Map) {
		if (this.compassOverlayEl) this.compassOverlayEl.remove();
		if (this.compassOverlayCleanup) this.compassOverlayCleanup();

		const container = map.getContainer() as HTMLElement;
		if (getComputedStyle(container).position === 'static') {
			container.style.position = 'relative';
		}

		const overlay = document.createElement('div');
		overlay.className = 'mapa-rosa-ventos-overlay';

		overlay.style.position = 'absolute';
		overlay.style.right = '95px';
		overlay.style.top = '70px';
		overlay.style.left = 'auto';
		overlay.style.width = '60px';
		overlay.style.height = '60px';
		overlay.style.pointerEvents = 'none';
		overlay.style.zIndex = '9999';

		container.appendChild(overlay);
		this.compassOverlayEl = overlay;

		const render = () => this.renderizarRosaDosVentos();
		map.whenReady(() => render());
		map.on('resize', render);

		this.compassOverlayCleanup = () => {
			map.off('resize', render);
		};
	}

	private renderizarRosaDosVentos() {
		if (!this.compassOverlayEl) return;

		const preto = '#111827';
		const cinza = '#9CA3AF';

		this.compassOverlayEl.innerHTML = `
			  <svg viewBox="0 0 100 100" width="60" height="60" style="display:block;">
			
				<!-- círculo -->
				<circle cx="50" cy="52" r="32" fill="none" stroke="${cinza}" stroke-width="2"/>
			
				<!-- estrela (pontas principais) -->
				<polygon points="50,18 57,52 50,86 43,52"
				  fill="${cinza}"/>
				<polygon points="18,52 50,59 82,52 50,45"
				  fill="${cinza}"/>
			
				<!-- pontas diagonais (cinza) -->
				<polygon points="50,26 54,52 50,78 46,52"
				  fill="${cinza}" opacity="0.9" transform="rotate(45 50 52)"/>
			
				<!-- miolo -->
				<circle cx="50" cy="52" r="4" fill="${cinza}"/>
			
				<!-- letras (PT-BR) -->
				<text x="50" y="10" text-anchor="middle" font-size="11" font-weight="700" fill="${preto}">N</text>
			
				<text x="76" y="22" text-anchor="middle" font-size="10" font-weight="700" fill="${preto}">NE</text>
				<text x="24" y="22" text-anchor="middle" font-size="10" font-weight="700" fill="${preto}">NO</text>
			
				<text x="92" y="55" text-anchor="middle" font-size="11" font-weight="700" fill="${preto}">L</text>
				<text x="8"  y="55" text-anchor="middle" font-size="11" font-weight="700" fill="${preto}">O</text>
			
				<text x="76" y="92" text-anchor="middle" font-size="10" font-weight="700" fill="${preto}">SE</text>
				<text x="24" y="92" text-anchor="middle" font-size="10" font-weight="700" fill="${preto}">SO</text>
			
				<text x="50" y="99" text-anchor="middle" font-size="11" font-weight="700" fill="${preto}">S</text>
			  
			  </svg>
			  `;
	}

	private getLeafletMap(): Leaflet.Map | null {
		return this.leafletMap ?? null;
	}

	private isMobileViewport(): boolean {
		const larguraViewport = Math.min(
			window.innerWidth || Number.MAX_SAFE_INTEGER,
			document.documentElement?.clientWidth || Number.MAX_SAFE_INTEGER,
			window.screen?.width || Number.MAX_SAFE_INTEGER
		);

		const isTouchDevice =
			'navigator' in window &&
			(/Android|iPhone|iPad|iPod|IEMobile|Opera Mini/i.test(
				window.navigator.userAgent
			) ||
				window.navigator.maxTouchPoints > 0);

		return larguraViewport <= 820 || isTouchDevice;
	}

	private async esperarRenderizacaoMapa() {
		await new Promise<void>(r => requestAnimationFrame(() => r()));
		await new Promise<void>(r => requestAnimationFrame(() => r()));
		await new Promise<void>(r => setTimeout(() => r(), 250));
	}

	private async ajustarLayoutMobileParaExportacao(
		root: HTMLElement,
		map: Leaflet.Map
	): Promise<(() => void) | null> {
		if (!this.isMobileViewport()) return null;

		const mapWrapper = root.querySelector('.map-wrapper') as HTMLElement | null;
		const leafletEl = root.querySelector(
			'.leaflet-container'
		) as HTMLElement | null;

		const rootPrev = {
			width: root.style.width,
			minWidth: root.style.minWidth,
			maxWidth: root.style.maxWidth,
		};
		const wrapperPrev = mapWrapper
			? {
					height: mapWrapper.style.height,
					minHeight: mapWrapper.style.minHeight,
			  }
			: null;
		const leafletPrev = leafletEl
			? {
					height: leafletEl.style.height,
			  }
			: null;

		root.style.width = `${this.exportDesktopWidth}px`;
		root.style.minWidth = `${this.exportDesktopWidth}px`;
		root.style.maxWidth = `${this.exportDesktopWidth}px`;

		if (mapWrapper) {
			mapWrapper.style.height = `${this.exportMapHeight}px`;
			mapWrapper.style.minHeight = `${this.exportMapHeight}px`;
		}
		if (leafletEl) {
			leafletEl.style.height = `${this.exportMapHeight}px`;
		}

		map.invalidateSize(true);
		await this.esperarRenderizacaoMapa();

		return () => {
			root.style.width = rootPrev.width;
			root.style.minWidth = rootPrev.minWidth;
			root.style.maxWidth = rootPrev.maxWidth;

			if (mapWrapper && wrapperPrev) {
				mapWrapper.style.height = wrapperPrev.height;
				mapWrapper.style.minHeight = wrapperPrev.minHeight;
			}
			if (leafletEl && leafletPrev) {
				leafletEl.style.height = leafletPrev.height;
			}

			map.invalidateSize(true);
		};
	}

	private getGeoJsonBounds(): Leaflet.LatLngBounds | undefined {
		const anyMapa = this.mapa as any;
		return anyMapa?.geoJsonLayer?.getBounds?.();
	}

	private instalarOverlaysNoMapa() {
		const map = this.getLeafletMap();

		console.log('[overlay] mapa existe?', !!map, 'mapa:', map);
		if (!map) return;

		map.whenReady(() => {
			this.aplicarContornoComoObservada();
		});
	}

	private aplicarMascaraForaDoMapa(geo: GeoJsonObject, map: Leaflet.Map) {
		if (this.maskLayer) {
			this.maskLayer.remove();
			this.maskLayer = undefined;
		}

		if (!map.getPane(this.maskPaneName)) {
			map.createPane(this.maskPaneName);
			const pane = map.getPane(this.maskPaneName)!;
			pane.style.zIndex = '350';
			pane.style.pointerEvents = 'none';
		}

		const holes: Leaflet.LatLngExpression[][] = [];

		const addPolygonOuterRingAsHole = (coords: any) => {
			if (!Array.isArray(coords) || !Array.isArray(coords[0])) return;

			const ring = coords[0] as [number, number][];
			const latlngs: Leaflet.LatLngExpression[] = ring.map(([lng, lat]) => [
				lat,
				lng,
			]);
			holes.push(latlngs);
		};

		const g: any = geo;

		if (g?.type === 'FeatureCollection' && Array.isArray(g.features)) {
			for (const f of g.features) {
				const geom = f?.geometry;
				if (!geom) continue;

				if (geom.type === 'Polygon') {
					addPolygonOuterRingAsHole(geom.coordinates);
				} else if (geom.type === 'MultiPolygon') {
					for (const polyCoords of geom.coordinates) {
						addPolygonOuterRingAsHole(polyCoords);
					}
				}
			}
		}

		const outer: Leaflet.LatLngExpression[] = [
			[90, -180],
			[90, 180],
			[-90, 180],
			[-90, -180],
		];

		this.maskLayer = Leaflet.polygon([outer, ...holes], {
			pane: this.maskPaneName,
			stroke: false,
			fillColor: '#FFFFFF',
			fillOpacity: 1,
			interactive: false,
		}).addTo(map);
	}

	private aplicarContornoComoObservada() {
		const map = this.getLeafletMap();
		if (!map || !this.pbGeo) return;

		if (!map.getPane(this.contornoPaneName)) {
			map.createPane(this.contornoPaneName);
			const pane = map.getPane(this.contornoPaneName)!;
			pane.style.zIndex = '450';
			pane.style.pointerEvents = 'none';
		}

		if (this.outlineLayer) {
			this.outlineLayer.remove();
			this.outlineLayer = undefined;
		}

		const strokeColor = '#111827';
		const strokeOpacity = this.isExporting ? 0.85 : 0.7;

		this.outlineLayer = Leaflet.geoJSON(this.pbGeo as any, {
			pane: this.contornoPaneName,
			interactive: false,
			style: () => ({
				color: strokeColor,
				opacity: strokeOpacity,
				weight: this.isExporting ? 1.4 : 1.0,
				fill: false,
				fillOpacity: 0,
				lineJoin: 'round',
				lineCap: 'round',
			}),
		}).addTo(map);

		this.outlineLayer.bringToFront();
	}

	private removerCoordenadas() {
		if (this.coordOverlayEl) {
			this.coordOverlayEl.remove();
			this.coordOverlayEl = undefined;
		}

		if (this.coordOverlayCleanup) {
			this.coordOverlayCleanup();
			this.coordOverlayCleanup = undefined;
		}
	}

	private removerRosaDosVentos() {
		if (this.compassOverlayEl) {
			this.compassOverlayEl.remove();
			this.compassOverlayEl = undefined;
		}

		if (this.compassOverlayCleanup) {
			this.compassOverlayCleanup();
			this.compassOverlayCleanup = undefined;
		}
	}

	private removerMascara() {
		if (this.maskLayer) {
			this.maskLayer.remove();
			this.maskLayer = undefined;
		}
	}

	private aplicarOverlaysSomenteNaExportacao(map: Leaflet.Map) {
		if (this.pbGeo) {
			this.aplicarMascaraForaDoMapa(this.pbGeo, map);
		}

		this.aplicarCoordenadas(map);
		this.aplicarRosaDosVentos(map);
	}

	private removerOverlaysDaExportacao() {
		this.removerCoordenadas();
		this.removerRosaDosVentos();
		this.removerMascara();
	}
}
